import { Link } from "react-router-dom";
import type { Match } from "@/mocks/matches";
import { teams } from "@/mocks/teams";

interface MatchCardProps {
  match: Match;
  variant?: "default" | "compact";
}

const statusMap: Record<Match["status"], { label: string; className: string }> = {
  live: { label: "直播中", className: "bg-accent-500 text-background-50" },
  upcoming: { label: "即将开始", className: "bg-secondary-100 text-secondary-900" },
  finished: { label: "已结束", className: "bg-background-200 text-foreground-600" },
};

export default function MatchCard({ match, variant = "default" }: MatchCardProps) {
  const home = teams.find((t) => t.id === match.homeTeamId);
  const away = teams.find((t) => t.id === match.awayTeamId);
  const status = statusMap[match.status];

  const hasScore = match.homeScore !== null && match.awayScore !== null;

  return (
    <div
      className={`group rounded-lg border border-background-200 bg-background-50 overflow-hidden transition-colors hover:border-primary-300 ${
        variant === "compact" ? "p-4" : "p-4 md:p-5"
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${status.className}`}
          >
            {match.status === "live" && (
              <span className="w-1.5 h-1.5 rounded-full bg-background-50 animate-pulse-live"></span>
            )}
            {status.label}
          </span>
          <span className="text-xs text-foreground-500 truncate">
            {match.stage} · {match.round}
          </span>
        </div>
        <span className="text-xs text-foreground-500 whitespace-nowrap shrink-0">
          {match.date.slice(5).replace("-", "/")} {match.time}
        </span>
      </div>

      <Link to={`/detail/${match.id}`} className="block">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="w-10 h-10 shrink-0 rounded-md bg-background-100 flex items-center justify-center overflow-hidden">
              {home ? (
                <img
                  src={home.crest}
                  alt={`${home.name}队徽`}
                  title={`${home.name} 欧冠球队`}
                  className="w-full h-full object-contain"
                />
              ) : (
                <i className="ri-shield-line text-foreground-400" aria-hidden="true"></i>
              )}
            </span>
            <span className="text-sm md:text-base font-medium text-foreground-900 truncate">
              {home?.name ?? "待定"}
            </span>
          </div>

          <div className="shrink-0 px-1 text-center">
            {hasScore ? (
              <span className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                {match.homeScore} : {match.awayScore}
              </span>
            ) : (
              <span className="font-heading text-base font-semibold text-foreground-400">VS</span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
            <span className="text-sm md:text-base font-medium text-foreground-900 truncate text-right">
              {away?.name ?? "待定"}
            </span>
            <span className="w-10 h-10 shrink-0 rounded-md bg-background-100 flex items-center justify-center overflow-hidden">
              {away ? (
                <img
                  src={away.crest}
                  alt={`${away.name}队徽`}
                  title={`${away.name} 欧冠球队`}
                  className="w-full h-full object-contain"
                />
              ) : (
                <i className="ri-shield-line text-foreground-400" aria-hidden="true"></i>
              )}
            </span>
          </div>
        </div>
      </Link>

      <div className="mt-4 pt-3 border-t border-background-200 flex items-center justify-between gap-2">
        <span className="text-xs text-foreground-500 truncate">{match.venue}</span>
        <div className="flex items-center gap-3 shrink-0">
          <Link
            to={`/detail/${match.id}`}
            className="text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
          >
            比赛详情
          </Link>
          {match.status === "live" && (
            <Link
              to={`/live/${match.id}`}
              className="inline-flex items-center gap-1 text-xs font-semibold text-accent-600 hover:text-accent-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-live-line" aria-hidden="true"></i>
              观看直播
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}